import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Download, Calendar, Info } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useDispatchVolume, useInactiveTechEmpnos, deptLabel, type DispatchVolRow } from "./api";

// ============================================================================
// Dispatch volume report — job counts from v_dispatch_full (all departments),
// sliced by tech / department / zone / arrival window / status / month. The
// operations view: where the work goes and who carries it.
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayISO(): string { return iso(new Date()); }
function yearsAgoISO(n: number): string { const d = new Date(); d.setFullYear(d.getFullYear() - n); return iso(d); }

type PresetKey = "last-90" | "this-year" | "last-year" | "last-12mo" | "custom";
function presetRange(key: PresetKey): { from: string; to: string } {
  const y = new Date().getFullYear();
  switch (key) {
    case "last-90": { const d = new Date(); d.setDate(d.getDate() - 90); return { from: iso(d), to: todayISO() }; }
    case "this-year": return { from: `${y}-01-01`, to: todayISO() };
    case "last-year": return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case "last-12mo": return { from: yearsAgoISO(1), to: todayISO() };
    default: return { from: `${y}-01-01`, to: todayISO() };
  }
}
const PRESETS: [PresetKey, string][] = [
  ["last-90", "Last 90 days"], ["this-year", "This year"], ["last-year", "Last year"],
  ["last-12mo", "Last 12 months"], ["custom", "Custom range"],
];

type GroupKey = "tech" | "dept" | "zone" | "window" | "status" | "month";
const GROUPS: [GroupKey, string][] = [
  ["tech", "Technician"], ["dept", "Department"], ["zone", "Zone (city)"],
  ["window", "Arrival window"], ["status", "Status"], ["month", "Month"],
];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

// ESC "pseudo-employees": 9 holding-buckets that aren't real technicians.
// Agreement-generated visits land on 9999 ("Serv Agr"); the rest are Sales /
// Parts / Reschedule / Return Installs / Follow Up / Pending Installs /
// Unassigned / Startups & Rebates. They're queue-lane concepts, not people
// (verified 2026-06-01 against mirror_employee). Matched by empno — the
// authoritative key the view exposes — and excluded ALWAYS: a holding-bucket
// is never a technician, so there is no user-facing toggle.
const PSEUDO_EMPNOS = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);
function isPseudoEmpno(empno: string | null): boolean {
  return PSEUDO_EMPNOS.has((empno ?? "").trim());
}

function groupValue(r: DispatchVolRow, by: GroupKey): string {
  switch (by) {
    case "tech": return (r.tech_name ?? "").trim() || "(unassigned)";
    case "dept": return deptLabel(r.tech_dept);
    case "zone": return (r.city ?? "").trim() || "(unknown zone)";
    case "window": { const w = (r.window_code ?? "").trim(); return !w || /^-+$/.test(w) ? "(no window)" : w.toUpperCase(); }
    case "status": return (r.status ?? "").trim() || "(no status)";
    case "month": return r.appointment_date.slice(0, 7);
  }
}

interface GroupRow { key: string; label: string; count: number; inactive?: boolean }

function StyledSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function DispatchReport() {
  const [preset, setPreset] = useState<PresetKey>("this-year");
  const [from, setFrom] = useState(() => presetRange("this-year").from);
  const [to, setTo] = useState(() => presetRange("this-year").to);
  const [groupBy, setGroupBy] = useState<GroupKey>("tech");
  const [activeOnly, setActiveOnly] = useState(true);
  const { data, isLoading, isError } = useDispatchVolume(from, to);
  const { data: inactiveSet } = useInactiveTechEmpnos();

  function choosePreset(key: PresetKey) {
    setPreset(key);
    if (key !== "custom") { const r = presetRange(key); setFrom(r.from); setTo(r.to); }
  }

  // Pseudo-employee exclusion is ALWAYS on (no toggle): the 9 holding-buckets
  // aren't technicians, so they never belong in a dispatch-volume view — on the
  // tech grouping OR any other (9999's agreement visits would otherwise pollute
  // dept / month / status too). The total excludes them as well, so percentages
  // stay honest. REPORT-07: inactive techs are tagged "(inactive)" and, by
  // default, hidden from the tech view ("Active techs only") — but they stay
  // countable when the toggle is off, so historical totals stay accurate.
  const { groups, total, pseudoCount, inactiveHidden } = useMemo(() => {
    const all = data?.rows ?? [];
    let rows = all.filter((r) => !isPseudoEmpno(r.tech_empno));
    const pseudoCount = all.length - rows.length;
    const inact = inactiveSet ?? new Set<string>();
    const byTechActive = groupBy === "tech" && activeOnly;
    let inactiveHidden = 0;
    if (byTechActive) {
      const before = rows.length;
      rows = rows.filter((r) => !inact.has((r.tech_empno ?? "").trim()));
      inactiveHidden = before - rows.length;
    }
    const map = new Map<string, number>();
    const empnoByKey = new Map<string, string>();
    for (const r of rows) {
      const k = groupValue(r, groupBy);
      map.set(k, (map.get(k) ?? 0) + 1);
      if (groupBy === "tech" && !empnoByKey.has(k)) empnoByKey.set(k, (r.tech_empno ?? "").trim());
    }
    let groups: GroupRow[] = Array.from(map.entries()).map(([key, count]) => ({
      key,
      label: groupBy === "month" ? monthLabel(key) : key,
      count,
      inactive: groupBy === "tech" ? inact.has(empnoByKey.get(key) ?? "") : false,
    }));
    groups = groupBy === "month" ? groups.sort((a, b) => a.key.localeCompare(b.key)) : groups.sort((a, b) => b.count - a.count);
    return { groups, total: rows.length, pseudoCount, inactiveHidden };
  }, [data, groupBy, activeOnly, inactiveSet]);

  const max = groups.reduce((m, g) => Math.max(m, g.count), 0);
  const TOP = 15;
  const chartGroups = groupBy === "month" ? groups : groups.slice(0, TOP);

  function exportCsv() {
    const groupLabel = GROUPS.find(([k]) => k === groupBy)?.[1] ?? groupBy;
    const lines: string[] = [];
    lines.push("Sedgwick Heating — Dispatch volume");
    lines.push(`Date range,${from} to ${to}`);
    lines.push(`Grouped by,${groupLabel}`);
    lines.push(`Total dispatches,${total}`);
    if (data?.truncated) lines.push("Note,Results capped — totals are a floor; narrow the range");
    lines.push("");
    lines.push(`${groupLabel},Dispatches,% of total`);
    for (const g of groups) {
      const pct = total > 0 ? ((g.count / total) * 100).toFixed(1) : "0.0";
      const safe = /[",\n]/.test(g.label) ? `"${g.label.replace(/"/g, '""')}"` : g.label;
      lines.push(`${safe},${g.count},${pct}%`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `dispatch-volume_${groupBy}_${from}_${to}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  return (
    <div>
      <Card className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Date range"><StyledSelect value={preset} onChange={(v) => choosePreset(v as PresetKey)} options={PRESETS} /></Field>
          <Field label="From"><input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="To"><input type="date" value={to} min={from} max={todayISO()} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="Group by"><StyledSelect value={groupBy} onChange={(v) => setGroupBy(v as GroupKey)} options={GROUPS} /></Field>
          {groupBy === "tech" && (
            <label className="flex h-9 cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30" />
              Active techs only
            </label>
          )}
          <div className="ml-auto"><Button variant="secondary" size="sm" onClick={exportCsv} disabled={groups.length === 0}><Download className="h-4 w-4" /> Export CSV</Button></div>
        </div>
      </Card>

      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-slate-600">
          {!isLoading && !isError && (
            <><span className="text-2xl font-bold tabular-nums text-slate-900">{total.toLocaleString()}</span><span className="ml-2">dispatches · {groups.length} {GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()} groups</span>
            {pseudoCount > 0 && <span className="text-slate-400"> · {pseudoCount.toLocaleString()} agreement/placeholder jobs excluded</span>}
            {inactiveHidden > 0 && <span className="text-slate-400"> · inactive techs hidden</span>}
            <InfoTip className="ml-2" label="How this report is figured">Counts each dispatch once, even with several techs, by appointment date across all departments. Agreement and placeholder jobs (Serv Agr, Sales, Parts, Reschedule, Unassigned and similar) are left out; they're holding spots in ESC, not technicians.</InfoTip></>
          )}
        </div>
        {data?.truncated && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700"><Info className="h-3.5 w-3.5" /> Results capped — narrow the range</span>}
      </div>

      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't load dispatches. Check the connection and try again.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading dispatches…</p></Card>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center"><Calendar className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No dispatches in this range</h3></Card>
      ) : (
        <>
          <Card className="mb-4 p-5">
            <h3 className="mb-4 text-[15px] font-bold tracking-tight text-slate-900">Dispatches by {GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()}{chartGroups.length < groups.length && <span className="font-normal text-slate-400"> · top {TOP}</span>}</h3>
            <div className="space-y-2.5">
              {chartGroups.map((g, i) => (
                <div key={g.key} className="flex items-center gap-3">
                  <div className={`w-40 flex-shrink-0 truncate text-right text-xs font-medium ${g.inactive ? "text-slate-400" : "text-slate-600"}`} title={g.label}>{g.label}{g.inactive ? " (inactive)" : ""}</div>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${max > 0 ? (g.count / max) * 100 : 0}%` }} transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.02, 0.25) }} className="h-full rounded-md bg-brand-500" />
                  </div>
                  <div className="w-20 flex-shrink-0 text-right text-xs tabular-nums"><span className="font-semibold text-slate-900">{g.count.toLocaleString()}</span><span className="ml-1 text-slate-400">{((g.count / total) * 100).toFixed(0)}%</span></div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">{GROUPS.find(([k]) => k === groupBy)?.[1]}</th>
                  <th className="px-4 py-2.5 text-right">Dispatches</th>
                  <th className="px-4 py-2.5 text-right">% of total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <tr key={g.key} className="transition-colors hover:bg-brand-50/40">
                    <td className={`px-4 py-2.5 font-medium ${g.inactive ? "text-slate-400" : "text-slate-800"}`}>{g.label}{g.inactive ? " (inactive)" : ""}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{g.count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{((g.count / total) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                  <td className="px-4 py-2.5 text-slate-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{total.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">100%</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}
